<template>
	<div>
		<div class="col-md-12">
			<div class="row">
				<div class="col-md-8">
					<h3 class="page-header">
						<i class="fa fa-sliders animated bounceInDown show-info"></i>
						Attendance Policies
					</h3>
					<p class="text-muted" style="margin-left:4px">
						A policy is a named rulebook. Factory floor, office staff and field sales can run
						different rules in the same company; each policy stores only what it changes.
					</p>
				</div>
				<div class="col-md-4 text-right" style="margin-top:20px">
					<button class="btn btn-primary" @click="create">
						<i class="fa fa-plus"></i> Add policy
					</button>
				</div>
			</div>
		</div>

		<div class="col-md-12" v-if="error" style="margin-top:10px">
			<div class="alert alert-danger">{{ error }}</div>
		</div>
		<div class="col-md-12 text-center" v-else-if="loading" style="margin-top:40px">
			<img src="../../assets/loading.gif" style="width:48px" alt="Loading" />
		</div>

		<div class="col-md-12" v-else-if="policies.length" style="margin-top:10px">
			<div class="row">
				<div class="col-md-4" v-for="policy in policies" :key="policy.id" style="margin-bottom:16px">
					<div class="panel policy-card">
						<div class="panel-body">
							<div class="flex-row" style="display:flex;justify-content:space-between">
								<div style="min-width:0">
									<a href="javascript:void(0)" class="text-primary font-weight-bold" @click="edit(policy)">
										{{ policy.name }}
									</a>
									<span v-if="policy.is_default" class="badge bg-info text-dark" style="margin-left:6px">Default</span>
									<span v-if="!policy.is_active" class="badge bg-secondary" style="margin-left:6px">Inactive</span>
									<br />
									<small class="text-muted">{{ policy.code }}</small>
								</div>
								<div style="white-space:nowrap">
									<button class="btn btn-sm btn-primary" @click="edit(policy)" title="Edit">
										<i class="fa fa-pencil"></i>
									</button>
									<button class="btn btn-sm btn-danger" @click="remove(policy)" title="Deactivate">
										<i class="fa fa-trash"></i>
									</button>
								</div>
							</div>

							<p v-if="policy.description" class="text-muted" style="margin-top:8px">
								{{ policy.description }}
							</p>

							<div style="margin:8px 0">
								<span
									v-for="(part, i) in overrideSummary(policy)"
									:key="i"
									class="badge bg-secondary"
									style="margin-right:4px;margin-bottom:4px;display:inline-block"
								>
									{{ part }}
								</span>
								<span v-if="!overrideSummary(policy).length" class="text-muted">
									Inherits every company setting
								</span>
							</div>

							<div class="flex-row" style="display:flex;justify-content:space-between;align-items:center">
								<span class="text-muted">
									{{ policy.employee_count }} employee{{ policy.employee_count === 1 ? '' : 's' }}
								</span>
								<button class="btn btn-link btn-sm" @click="showEffective(policy)">
									View resolved rules
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- First run: this is where the concept needs explaining most -->
		<div class="col-md-12" v-else style="margin-top:20px">
			<div class="panel">
				<div class="panel-body text-center" style="padding:40px">
					<i class="fa fa-sliders" style="font-size:32px;color:#94a3b8"></i>
					<p class="font-weight-bold" style="margin-top:12px">No policies yet</p>
					<p class="text-muted" style="max-width:520px;margin:8px auto">
						Attendance works without one — everybody just follows the company settings. You need a
						policy as soon as one group has to differ: night-shift staff on a rota, or a sales team
						that should not be geofenced.
					</p>
					<button class="btn btn-primary" @click="create">
						<i class="fa fa-plus"></i> Create your first policy
					</button>
				</div>
			</div>
		</div>

		<!-- Resolved rules -->
		<modal name="attendance-effective" :width="720" :height="'auto'" :adaptive="true" :scrollable="true">
			<div style="padding:18px">
				<h4 class="modal-header-title">Resolved rules — {{ effectiveName }}</h4>
				<p class="text-muted">
					The company defaults with this policy's changes applied on top — the complete set of
					rules an employee on this policy actually gets. Use it to check that an override landed
					where you expected, since the policy itself only stores the differences.
				</p>
				<div class="text-center" v-if="loadingEffective" style="padding:20px">
					<img src="../../assets/loading.gif" style="width:40px" alt="Loading" />
				</div>
				<pre v-else-if="effectiveData" class="effective-json">{{ prettyEffective }}</pre>
				<div class="text-right" style="margin-top:12px">
					<button class="btn btn-default" @click="$modal.hide('attendance-effective')">Close</button>
				</div>
			</div>
		</modal>
	</div>
</template>

<script>
import { policyService, errorMessage } from './attendanceService'

export default {
	name: 'AttendancePolicies',
	data() {
		return {
			policies: [],
			loading: false,
			error: '',
			effectiveData: null,
			effectiveName: '',
			loadingEffective: false,
		}
	},
	computed: {
		prettyEffective() {
			return this.effectiveData ? JSON.stringify(this.effectiveData.effective, null, 2) : ''
		},
	},
	mounted() {
		this.refresh()
	},
	methods: {
		refresh() {
			this.loading = true
			this.error = ''
			policyService
				.list()
				.then((data) => {
					this.policies = data || []
				})
				.catch((e) => {
					this.error = errorMessage(e, 'Could not load policies.')
				})
				.finally(() => {
					this.loading = false
				})
		},
		create() {
			this.$router.push({ name: 'attendance-policy-new' })
		},
		edit(policy) {
			this.$router.push({ name: 'attendance-policy-edit', params: { id: policy.id } })
		},
		remove(policy) {
			const message =
				policy.employee_count > 0
					? `${policy.employee_count} employee(s) still use "${policy.name}". Deactivate it anyway?`
					: `Deactivate "${policy.name}"?`
			if (!window.confirm(message)) return
			policyService
				.remove(policy.id)
				.then(() => {
					this.$notify({ text: 'Deactivated', type: 'success' })
					this.refresh()
				})
				.catch((e) => {
					this.$notify({ text: errorMessage(e, 'Failed'), type: 'error' })
				})
		},
		showEffective(policy) {
			this.effectiveName = policy.name
			this.loadingEffective = true
			this.effectiveData = null
			this.$modal.show('attendance-effective')
			policyService
				.effective(policy.id)
				.then((data) => {
					this.effectiveData = data
				})
				.catch((e) => {
					this.$notify({ text: errorMessage(e, 'Failed'), type: 'error' })
				})
				.finally(() => {
					this.loadingEffective = false
				})
		},
		/** One-line summary of what a policy actually overrides. */
		overrideSummary(policy) {
			const config = policy.config || {}
			const parts = []
			if (config.shift && config.shift.mode) parts.push(`shift: ${config.shift.mode}`)
			if (config.geo && config.geo.outside_action)
				parts.push(`outside fence: ${config.geo.outside_action}`)
			if (config.overtime && config.overtime.enabled !== undefined)
				parts.push(`overtime: ${config.overtime.enabled ? 'on' : 'off'}`)
			if (config.day_rules && 'late_after_min' in config.day_rules)
				parts.push(
					config.day_rules.late_after_min === null
						? 'never late'
						: `grace: ${config.day_rules.late_after_min} min`
				)
			if (config.day_rules && config.day_rules.min_hours_full !== undefined)
				parts.push(`full day: ${config.day_rules.min_hours_full}h`)
			return parts
		},
	},
}
</script>

<style scoped>
.modal-header-title {
	margin-top: 0;
}
.policy-card {
	height: 100%;
}
.effective-json {
	max-height: 380px;
	overflow: auto;
	background: #f7f9fc;
	padding: 14px;
	font-size: 12px;
	border: 1px solid #e5e9f2;
}
</style>
